<!--
 * Path: packages/frontend/src/features/browser/forms/Rating.vue
 * Task: Renders a 5-star rating input utilizing the new FormBlockWrapper.
 * Refactoring: Shifted state and save targets to the isolated workspace and entity operation modules.
-->
<template>
  <FormBlockWrapper
    v-if="activeEntity"
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="props.field.name"
    :info="props.field.info"
    :can-edit="canEdit"
    :disable-edit="props.field.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="showInfo"
  >
    <template #read>
      <div class="d-flex align-center ms-1">
        <v-rating
          :model-value="fieldValueProxy"
          color="warning"
          density="compact"
          half-increments
          readonly
          size="small"
        ></v-rating>
        <span v-if="!fieldValueProxy && fieldValueProxy !== 0" class="text-body-2 text-disabled ms-2">-</span>
        <span v-else class="text-caption font-weight-bold ms-2">{{ fieldValueProxy }} / 5</span>
      </div>
    </template>

    <template #edit>
      <div class="d-flex flex-column align-start">
        <div class="d-flex align-center">
          <v-rating
            v-model="fieldValueProxy"
            color="warning"
            active-color="warning"
            density="comfortable"
            half-increments
            hover
            clearable
          ></v-rating>
          <span class="text-body-2 font-weight-bold ms-3">{{ fieldValueProxy || 0 }}</span>
        </div>
        <div v-if="errorMessages" class="text-error text-caption mt-1">{{ errorMessages }}</div>
      </div>
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';

export interface FieldProps {
  id: string;
  type: string;
  name: string;
  info?: string;
  readonly?: boolean;
}

const props = defineProps<{ field: FieldProps; errorMessages?: string | string[] }>();

const emit = defineEmits<{
  (e: 'show-info', payload: { title: string; text?: string }): void;
}>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);
const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.field.readonly);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

// Clean proxy for reading and writing to the globally injected entity state.
const fieldValueProxy = computed({
  get: () => {
    if (!activeEntity.value || !props.field.id) return 0;
    const val = activeEntity.value[props.field.id];
    return typeof val === 'number' ? val : 0;
  },
  set: (newValue: number | null) => {
    if (activeEntity.value && props.field.id) {
      activeEntity.value[props.field.id] = newValue || 0;
    }
  }
});

const showInfo = (): void => {
  emit('show-info', { title: props.field.name, text: props.field.info });
};
</script>
